import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ProductLabelRecommendEnum } from 'src/admin-entity/enum/product/product-label-recommend.enum';
import { ProductLabelShippingMethodEnum } from 'src/admin-entity/enum/product/product-label-shipping-method.enum';
import { ProductStatus } from 'src/admin-entity/enum/product/product-status.enum';
import { Page } from 'src/admin-entity/interface/page/page-info.interface';
import { ProductBasicInfo } from 'src/admin-entity/interface/product/custom/product-basic-info.interface';
import {
    ProductPurchaseCondition,
    UpdateProductPurchaseConditionBody,
} from 'src/admin-entity/interface/product/custom/product-purchase-condition.interface';
import { HomeDisplayWithProduct } from 'src/admin-entity/interface/product/product-home-display-with-product.interface';
import { ProductLog } from 'src/admin-entity/interface/product/product-log.interface';
import { ProductOld } from 'src/admin-entity/interface/product/product-old.interface';
import { ProductOptionCombination } from 'src/admin-entity/interface/product/product-option-combination.interface';
import { ProductOptionGroup } from 'src/admin-entity/interface/product/product-option-group.interface';
import { ProductPage } from 'src/admin-entity/interface/product/product-page.interface';
import { ProductSaleType } from 'src/admin-entity/interface/product/product-sale-type.interface';
import { ProductTag } from 'src/admin-entity/interface/product/product-tag.interface';
import { ProductVideo } from 'src/admin-entity/interface/product/product-video.interface';
import { PropertyJsonDto, RegisterProductImageDto } from 'src/admin-entity/interface/product/product.interface';
import { BASE_URL } from './../config/api';
import client from './client';
import { HomeDisplayCategory } from 'src/admin-entity/interface/home-display/home-display-category.interface';
import { ProductRelated } from 'src/admin-entity/interface/product/product-related.interface';
import { ProductRelatedProduct } from 'src/admin-entity/interface/product/product-related-product.interface';
import { ProductTagGroup } from 'src/admin-entity/interface/product/product-tag-group.interface';
import { ProductImageRepresent } from 'src/admin-entity/interface/product/product-image-represent.interface';
import {
    CreateGlobalShowDirectionBody,
    GlobalShowDirection,
    UpdateGlobalShowDirectionBody,
} from 'src/admin-entity/interface/live/live-global-show-direction.interface';

export const getProduct = (productId: number): Promise<AxiosResponse<any>> =>
    client.get(`${BASE_URL}/v1/products/${productId}`);

export const getProductDetailInfo = (
    productId: number,
): Promise<
    AxiosResponse<{
        id: number;
        name: string;
        productDetail: {
            id: number;
            optionIdPath: any;
            productOptionCombination: ProductOptionCombination;
        };
        productSatus: ProductStatus;
        totalOptionStock: number;
    }>
> => client.get(`${BASE_URL}/v1/products/${productId}/options`);

export const createSimpleProduct = (): Promise<AxiosResponse<{ id: number }>> =>
    client.post(`${BASE_URL}/v1/admin/products/simple`);

export const createProductVideo = (productVideo: ProductVideo): Promise<AxiosResponse<ProductVideo>> =>
    client.post(`${BASE_URL}/v1/admin/video`, productVideo);

export const addProduct = (product: ProductOld, config?: AxiosRequestConfig): Promise<AxiosResponse<ProductOld>> =>
    client.post(`${BASE_URL}/v1/admin/products`, product, config);

export const addProductRepresentImage = (
    imgFiles: File[],
    productId: number,
    config?: AxiosRequestConfig,
): Promise<AxiosResponse<any>> => {
    const url = `${BASE_URL}/v2/admin/product/${productId}/image/represent`;
    const form = new FormData();
    for (let i = 0; i < imgFiles.length; i++) {
        form.append(`imgFiles`, imgFiles[i]);
    }

    return client.post(url, form, config);
};

export const addProductImages = (
    imgFiles: File[],
    productId: number,
    config?: AxiosRequestConfig,
): Promise<AxiosResponse<any>> => {
    const url = `${BASE_URL}/v2/admin/product/${productId}/image`;
    const form = new FormData();
    for (let i = 0; i < imgFiles.length; i++) {
        form.append(`imgFiles`, imgFiles[i]);
    }

    return client.post(url, form, config);
};

export const getProductList = (url: string): Promise<AxiosResponse<ProductPage>> => client.get(`${url}`);

export const getVideoList = (url: string): Promise<AxiosResponse<Page<ProductVideo>>> => client.get(`${url}`);

export const getProductDetail = (id: number): Promise<AxiosResponse<ProductOld>> =>
    client.get(`${BASE_URL}/v1/admin/products/${id}`);

export const getProductVideoDetail = (id: number): Promise<AxiosResponse<ProductVideo>> =>
    client.get(`${BASE_URL}/v1/admin/video/${id}`);

export const searchProduct = (productName: string, distributorName: string): Promise<AxiosResponse<ProductPage>> => {
    const url = new URL(`${BASE_URL}/v1/admin/products/search`);
    if (productName) {
        url.searchParams.set('productName', productName);
    }
    if (distributorName) {
        url.searchParams.set('distributorName', distributorName);
    }
    url.searchParams.set('filter', 'latest');
    return client.get<ProductPage>(`${url.toString()}`);
};

export const updateRepresentImage = (
    body: {
        productId: number;
        representImageFile: any;
    },
    config?: AxiosRequestConfig,
): Promise<AxiosResponse<RegisterProductImageDto>> =>
    client.put(`${BASE_URL}/v1/admin/product-image/product-represent-image`, body, config);

export const addProductImageRepresent = (
    productId: number,
    imgFiles: string[],
    setProgress: (value: any) => void,
): Promise<AxiosResponse<ProductImageRepresent[]>> => {
    const url = `${BASE_URL}/v2/admin/product/${productId}/image/represent`;
    const form = new FormData();
    imgFiles.forEach(imgFile => {
        form.append(`imgFiles`, imgFile);
    });
    return client.post(url, form, {
        onUploadProgress: (e: ProgressEvent) => {
            setProgress(Math.floor((e.loaded / e.total) * 100));
        },
    });
};

export const sortingProductImageRepresent = (
    productId: number,
    body: { id: number; sort: number }[],
): Promise<AxiosResponse<{ id: number }[]>> => {
    return client.put(`${BASE_URL}/v2/admin/product/${productId}/image/represent/sort`, body);
};

export const deleteProductImageRepresent = (productId: number, ids: number[]): Promise<AxiosResponse<any>> => {
    return client.put(`${BASE_URL}/v2/admin/product/${productId}/image/represent/del`, ids);
};

export const addProductImage = (
    body: {
        productId: number;
        productImages: RegisterProductImageDto[];
        // productImageFile: any;
        // sort: number;
    },
    config?: AxiosRequestConfig,
): Promise<AxiosResponse<RegisterProductImageDto[]>> => client.post(`${BASE_URL}/v1/admin/product-image`, body, config);

export const deleteProductImage = (productId: number, ids: number[]): Promise<AxiosResponse<boolean>> =>
    client.put(`${BASE_URL}/v2/admin/product/${productId}/image/del`, ids);

export const disconnectProduct = (videoId: number, productId: number): Promise<AxiosResponse<boolean>> =>
    client.delete(`${BASE_URL}/v1/admin/video/${videoId}/product/${productId}/link`);

export const deleteProductVideo = (videoId: number): Promise<AxiosResponse<boolean>> =>
    client.delete(`${BASE_URL}/v1/admin/video/${videoId}`);

export const connectProduct = (videoId: number, productId: number): Promise<AxiosResponse<boolean>> =>
    client.post(`${BASE_URL}/v1/admin/video/${videoId}/product/${productId}/link`);

export const updateProductVideoBase = (productVideo: ProductVideo): Promise<AxiosResponse<ProductVideo>> =>
    client.put(`${BASE_URL}/v1/admin/video/${productVideo.id}/base`, productVideo);

export const updateProductVideoDetail = (productVideo: ProductVideo): Promise<AxiosResponse<ProductVideo>> =>
    client.put(`${BASE_URL}/v1/admin/video/${productVideo.id}/detail`, productVideo);

export const updateProductImageSort = (
    productId: number,
    body: { id: number; sort: number }[],
): Promise<AxiosResponse<boolean>> => client.put(`${BASE_URL}/v2/admin/product/${productId}/image/sort`, body);

export const updateProductBasicInfo = (
    productId: string,
    body: ProductBasicInfo,
): Promise<AxiosResponse<ProductBasicInfo>> => client.put(`${BASE_URL}/v1/admin/products/base/${productId}`, body);

export const updateProductLink = (
    productId: string,
    body: {
        deeplink: string;
        productDetail: {
            videoUrl: string;
        };
    },
): Promise<AxiosResponse<{ deeplink: string; videoUrl: string }>> =>
    client.put(`${BASE_URL}/v1/admin/products/link/${productId}`, body);

export const updateProductLabel = (
    productId: string,
    labelShippingMethod: ProductLabelShippingMethodEnum,
    labelRecommend: ProductLabelRecommendEnum,
): Promise<AxiosResponse<boolean>> =>
    client.put(`${BASE_URL}/v1/admin/products/label/${productId}`, {
        labelShippingMethod,
        labelRecommend,
    });

export const updateProductOption = (
    productId: string,
    body: {
        restrictedStockOrder: number;
        isExclusive: boolean;
        productOrderConditions: string[];
        shipmentDateOption: string;
        shipmentDateOptionType: 1 | 2 | 3;
        productDetail: {
            id: number;
            productOptionGroups: ProductOptionGroup[];
            productOptionCombinations: ProductOptionCombination[];
        };
        productSaleType: ProductSaleType;
        isRepurchasable?: boolean;
        minPurchaseQty?: number;
        maxPurchaseQty?: number;
    },
): Promise<AxiosResponse<any>> => client.put(`${BASE_URL}/v1/admin/products/option/${productId}`, body);

export const updateProductDisplay = (
    productId: string,
    body: {
        productStatus: ProductStatus;
        productSaleType: ProductSaleType;
        category: {
            id: number;
        };
        useGift: boolean;
        giftExpireDays: number;
        giftExpireType: 'FIXED_DATE' | 'AFTER_DAYS';
        giftFixedExpireDate: Date;
        isVisibleInList: boolean;
        minAppUserGrade: number;
        homeDisplayWithProduct: HomeDisplayWithProduct[];
        productRelated: ProductRelated;
        isAppOnly: boolean;
        productTagGroupIds: number[];
        tags: { tag: string }[];
    },
): Promise<AxiosResponse<any>> => client.put(`${BASE_URL}/v1/admin/products/display/${productId}`, body);

export const getProductTagGroups = (): Promise<AxiosResponse<ProductTagGroup[]>> =>
    client.get(`${BASE_URL}/v1/admin/products/tag-group`);

export const getChildrenTagList = (id: number): Promise<AxiosResponse<ProductTag[]>> =>
    client.get(`${BASE_URL}/v1/admin/products/tag-group/${id}`);

export const addTagGroup = (title: string): Promise<AxiosResponse<any>> =>
    client.post(`${BASE_URL}/v1/admin/products/tag-group`, { title });

export const addChildTag = (parentId: number, tagName: string): Promise<AxiosResponse<ProductTagGroup>> =>
    client.post(`${BASE_URL}/v1/admin/products/tag-group`, {
        parentId,
        tag: tagName,
    });

export const addProductsToTag = (tagGroupId: number, productIds: number[]): Promise<AxiosResponse<any>> =>
    client.post(`${BASE_URL}/v1/admin/products/tag-group/${tagGroupId}/bulk-product`, productIds);

export const deleteProductsFromTag = (tagGroupId: number, productIds: number[]): Promise<AxiosResponse<any>> =>
    client.put(`${BASE_URL}/v1/admin/products/tag-group/${tagGroupId}/bulk-product/delete`, productIds);

export const deleteTag = (tagId: number): Promise<AxiosResponse<any>> =>
    client.delete(`${BASE_URL}/v1/admin/products/tag-group/${tagId}`);

export const updateParentTag = (parentTagId: number, title: string): Promise<AxiosResponse<ProductTagGroup>> => {
    return client.put(`${BASE_URL}/v1/admin/products/tag-group/${parentTagId}`, { title });
};

export const updateChildTag = (childTagId: number, tag: string): Promise<AxiosResponse<ProductTag>> => {
    return client.put(`${BASE_URL}/v1/admin/products/tag-group/${childTagId}`, { tag });
};

export const getProductTagGroupDetail = (tagGroupId: number): Promise<AxiosResponse<ProductTagGroup>> => {
    return client.get(`${BASE_URL}/v1/admin/products/tag-group/${tagGroupId}`);
};

export const updateProductDeliveryInfo = (
    productId: string,
    body: {
        distributor: {
            id: number;
        };
        deliveryCompany: {
            id: number;
        };
        unstoringDaysAfter: number;
        productShippingFee: {
            shippingFee: number;
            primaryAreaShippingFee: number;
            secondaryAreaShippingFee: number;
            thirdAreaShippingFee: number;
        };
        freeShippingFeeConditionPrice: number;
        isBundleByDistributor: boolean;
        maxUnitInBox: number;
        bundleShippingFeeType: 0 | 1 | 2;
        isUndeliverableSecondaryarea: boolean;
        isUndeliverableThirdarea: boolean;
    },
): Promise<AxiosResponse<any>> => client.put(`${BASE_URL}/v1/admin/products/delivery-info/${productId}`, body);

export const updateProductDistributor = (
    productId: string,
    body: {
        distributor: {
            id: number;
        };
    },
): Promise<AxiosResponse<any>> => client.put(`${BASE_URL}/v1/admin/products/distributor/${productId}`, body);

export const updateProductTag = (
    productId: string,
    body: {
        tags: ProductTag[];
    },
): Promise<AxiosResponse<any>> => client.put(`${BASE_URL}/v1/admin/products/tag/${productId}`, body);

export const updateProductPropertyJson = (
    productId: string,
    body: {
        productDetail: {
            propertyJson: PropertyJsonDto[];
        };
    },
): Promise<AxiosResponse<any>> => client.put(`${BASE_URL}/v1/admin/products/property-json/${productId}`, body);

export const updateProductAll = (
    productId: string,
    body: {
        base: any;
        link: any;
        option: any;
        display: any;
        deliveryInfo: any;
        distributor: any;
        propertyJson: any;
    },
): Promise<AxiosResponse<any>> => client.put(`${BASE_URL}/v1/admin/products/all/${productId}`, body);

export const getProductLogs = (id: number): Promise<AxiosResponse<Page<ProductLog>>> =>
    client.get(`${BASE_URL}/v1/admin/log/product/${id}`);

export const addViewCount = (): Promise<AxiosResponse<any>> =>
    client.put(`${BASE_URL}/v1/admin/video/update-view-count`);

export const getExcellAll = (
    search: string,
): Promise<
    AxiosResponse<{
        page: {
            count: number;
        };
        products: ProductOld[];
    }>
> => {
    return client.get(`${BASE_URL}/v1/admin/products/search/excel-all${search}`);
};

export const getExcelItems = (
    ids: number[],
): Promise<
    AxiosResponse<{
        page: {
            count: number;
        };
        products: ProductOld[];
    }>
> => {
    return client.get(`${BASE_URL}/v1/admin/products/search/excel?ids=${ids.join('|')}`);
};

export const getGradeList = (): Promise<AxiosResponse<{ grade: number; text: string }[]>> => {
    return client.get(`${BASE_URL}/v1/admin/products/grades`);
};

export const getTrackingLinkInfo = (
    id: number,
): Promise<
    AxiosResponse<{
        instagramLink: string;
        naverBandLink: string;
        kakaoChannelLink: string;
        youtubeLink: string;
        tiktokLink: string;
    }>
> => {
    return client.get(`${BASE_URL}/v1/admin/products/tracking-link/${id}`);
};

export const updateTrackingLinkInfo = (id: number, link: { [key: string]: string }): Promise<AxiosResponse<boolean>> =>
    client.put(`${BASE_URL}/v1/admin/products/tracking-link/${id}`, link);

export const getProductRelated = (): Promise<AxiosResponse<HomeDisplayCategory>> =>
    client.get(`${BASE_URL}/v1/admin/display-category/key/product-related/simple`);

export const removeProductFromProductRelatedProducts = (
    productId: number,
    ids: number[],
): Promise<AxiosResponse<ProductRelatedProduct[]>> =>
    client.post(`${BASE_URL}/v1/admin/products/${productId}/product-related/del`, ids);

export const addProductToProductRelatedProducts = (
    productId: number,
    ids: number[],
): Promise<AxiosResponse<ProductRelatedProduct[]>> =>
    client.post(`${BASE_URL}/v1/admin/products/${productId}/product-related`, ids);

export const updatePriorityProductRelatedProducts = (
    productId: number,
    priorities: { id: number; priority: number }[],
): Promise<AxiosResponse<ProductRelatedProduct[]>> =>
    client.put(`${BASE_URL}/v1/admin/products/${productId}/product-related/priority`, priorities);

export const productInvoice = (productId: number): Promise<AxiosResponse<any>> => {
    return client.get(`${BASE_URL}/v1/admin/products/${productId}/count/invoice`);
};

export const updateFundingSalesQuantity = (productId: number, salesQuantity: number): Promise<AxiosResponse<any>> => {
    return client.put(`${BASE_URL}/v1/admin/products/funding/${productId}`, { salesQuantity });
};

export const updateLiveProductStatus = (productId: number, productStatus: string): Promise<AxiosResponse<any>> => {
    return client.put(`${BASE_URL}/v1/admin/products/product-status/${productId}`, { productStatus });
};

export const purchaseCountsReset = (productId: number): Promise<AxiosResponse<any>> =>
    client.delete(`${BASE_URL}/v1/admin/products/${productId}/purchase-counts/reset`);

export const copyProduct = (productId: number): Promise<AxiosResponse<any>> =>
    client.post(`${BASE_URL}/v1/admin/products/${productId}/copy`);

export const getGlobalShowDirectionList = (): Promise<AxiosResponse<GlobalShowDirection[]>> =>
    client.get(`${BASE_URL}/v1/admin/ls/global-settings/show-direction`);

export const createGlobalShowDirection = (
    body: CreateGlobalShowDirectionBody,
): Promise<AxiosResponse<GlobalShowDirection[]>> =>
    client.post(`${BASE_URL}/v1/admin/ls/global-settings/show-direction`, body);

export const updateGlobalShowDirection = (
    id: number,
    body: UpdateGlobalShowDirectionBody,
): Promise<AxiosResponse<GlobalShowDirection[]>> =>
    client.put(`${BASE_URL}/v1/admin/ls/global-settings/show-direction/${id}`, body);

export const updateGlobalShowDirectionEnabled = (
    id: number,
    isEnabled: boolean,
): Promise<AxiosResponse<GlobalShowDirection[]>> =>
    client.put(`${BASE_URL}/v1/admin/ls/global-settings/show-direction/${id}`, { isEnabled });

export const deleteGlobalShowDirection = (id: number): Promise<AxiosResponse<GlobalShowDirection[]>> =>
    client.delete(`${BASE_URL}/v1/admin/ls/global-settings/show-direction/${id}`);

export const updateGlobalShowDirectionSortOrder = (body: number[]): Promise<AxiosResponse<GlobalShowDirection[]>> =>
    client.put(`${BASE_URL}/v1/admin/ls/global-settings/show-direction/sort-order`, body);

export const getProductPurchaseCondition = (productId: number): Promise<AxiosResponse<ProductPurchaseCondition>> =>
    client.get(`${BASE_URL}/v1/admin/products/${productId}/purchase-condition`);

export const updateProductPurchaseCondition = (
    productId: number,
    body: UpdateProductPurchaseConditionBody,
): Promise<AxiosResponse<ProductPurchaseCondition>> =>
    client.put(`${BASE_URL}/v1/admin/products/${productId}/purchase-condition`, body);

export const updateLiveProductTypeAndSpecialSaleAll = (
    lsBroadcastId: number,
    body: {
        productIds: number[];
        productType?: number;
        isSpecialSale?: boolean;
    },
): Promise<AxiosResponse<any>> =>
    client.put(`${BASE_URL}/v1/admin/ls/${lsBroadcastId}/product/all/type-special-sale`, body);
